import {
  BaseFieldFilter,
  CustomFieldFilter,
  NativeFieldFilter,
  SimpleSearchFieldFilter,
} from 'api/dashboard/widget';
import {
  AssigneeField,
  BooleanField,
  DateField,
  EstimateField,
  FieldType,
  FilterableField,
  MultiSelectField,
  MultiUserField,
  NumberField,
  SingleSelectField,
  SingleUserField,
  TextField,
} from 'components/organisms/tasks/fields';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { AssigneeFieldFilter } from 'components/organisms/tasks/filters/assignee';
import { BooleanFieldFilter } from 'components/organisms/tasks/filters/boolean';
import { DateFieldFilter } from 'components/organisms/tasks/filters/date';
import { EstimateFieldFilter } from 'components/organisms/tasks/filters/estimate';
import { MultiSelectFieldFilter } from 'components/organisms/tasks/filters/multi-select';
import { MultiUserFieldFilter } from 'components/organisms/tasks/filters/multi-user';
import { NumberFieldFilter } from 'components/organisms/tasks/filters/number';
import { Operation } from 'components/organisms/tasks/filters/operations';
import { GroupSeparator } from 'components/organisms/tasks/filters/separators';
import { SingleSelectFieldFilter } from 'components/organisms/tasks/filters/single-select';
import { SingleUserFieldFilter } from 'components/organisms/tasks/filters/single-user';
import { TextFieldFilter } from 'components/organisms/tasks/filters/text';
import { GroupCriteria } from 'components/organisms/tasks/grouped-filter-popover';
import { unreachable } from '../unreachable';
import { AssigneeFilter } from './fields/assignee';
import { BooleanFilter } from './fields/boolean';
import { DateFilter } from './fields/date';
import { EstimateFilter } from './fields/estimate';
import { MultiSelectFilter } from './fields/multi-select';
import { MultiUserFilter } from './fields/multi-user';
import { NumberFilter } from './fields/number';
import { SingleSelectFilter } from './fields/single-select';
import { SingleUserFilter } from './fields/single-user';
import { TextFilter } from './fields/text';

export const toApiFormat = (filter: FieldFilter): BaseFieldFilter => {
  switch (filter.field.type) {
    case FieldType.Boolean:
      return BooleanFilter.toApiFormat(filter as BooleanFieldFilter);
    case FieldType.Number:
      return NumberFilter.toApiFormat(filter as NumberFieldFilter);
    case FieldType.Date:
      return DateFilter.toApiFormat(filter as DateFieldFilter);
    case FieldType.Estimate:
      return EstimateFilter.toApiFormat(filter as EstimateFieldFilter);
    case FieldType.Text:
    case FieldType.MultiLineText:
      return TextFilter.toApiFormat(filter as TextFieldFilter);
    case FieldType.SingleSelect:
      return SingleSelectFilter.toApiFormat(filter as SingleSelectFieldFilter);
    case FieldType.MultiSelect:
      return MultiSelectFilter.toApiFormat(filter as MultiSelectFieldFilter);
    case FieldType.SingleUser:
      return SingleUserFilter.toApiFormat(filter as SingleUserFieldFilter);
    case FieldType.MultiUser:
      return MultiUserFilter.toApiFormat(filter as MultiUserFieldFilter);
    case FieldType.Assignee:
      return AssigneeFilter.toApiFormat(filter as AssigneeFieldFilter);
    default:
      return unreachable(filter.field);
  }
};

const parse = (
  filter: SimpleSearchFieldFilter,
  field: FilterableField,
): FieldFilter => {
  const fieldType = field.type;

  switch (fieldType) {
    case FieldType.Boolean:
      return BooleanFilter.parse(
        filter as NativeFieldFilter,
        field as BooleanField,
      );
    case FieldType.Number:
      return NumberFilter.parse(
        filter as NativeFieldFilter,
        field as NumberField,
      );
    case FieldType.Date:
      return DateFilter.parse(filter as NativeFieldFilter, field as DateField);
    case FieldType.Text:
    case FieldType.MultiLineText:
      return TextFilter.parse(filter as NativeFieldFilter, field as TextField);
    case FieldType.SingleSelect:
      return SingleSelectFilter.parse(
        filter as NativeFieldFilter,
        field as SingleSelectField,
      );
    case FieldType.MultiSelect:
      return MultiSelectFilter.parse(
        filter as NativeFieldFilter,
        field as MultiSelectField,
      );
    case FieldType.SingleUser:
      return SingleUserFilter.parse(
        filter as NativeFieldFilter,
        field as SingleUserField,
      );
    case FieldType.MultiUser:
      return MultiUserFilter.parse(
        filter as NativeFieldFilter,
        field as MultiUserField,
      );
    case FieldType.Estimate:
      return EstimateFilter.parse(
        filter as NativeFieldFilter,
        field as EstimateField,
      );
    case FieldType.Assignee:
      return AssigneeFilter.parse(
        filter as NativeFieldFilter,
        field as AssigneeField,
      );
    default:
      return unreachable(fieldType);
  }
};

const toNativeFieldFilterFormat = (filter: FieldFilter): NativeFieldFilter => {
  return {
    name: filter.field.fieldName,
    ...toApiFormat(filter),
  };
};

const toCustomFieldFilterFormat = (filter: FieldFilter): CustomFieldFilter => {
  return {
    fieldId: filter.field.fieldId,
    ...toApiFormat(filter),
  };
};

const getValueForQueryString = (
  filter: NativeFieldFilter | CustomFieldFilter,
) => {
  switch (filter.operation) {
    case Operation.IsEmpty:
    case Operation.IsNotEmpty:
    case Operation.IsNull:
      return true;
    default:
      return filter.value ? encodeURIComponent(filter.value) : undefined;
  }
};

const nativeFieldToQueryStringFormat = (field: NativeFieldFilter): string =>
  `${field.sourceType}${GroupSeparator}${field.name}.${
    field.operation
  }=${getValueForQueryString(field)}`;

const customFieldToQueryStringFormat = (field: CustomFieldFilter): string =>
  `${field.sourceType}${GroupSeparator}field.${field.fieldId}.${
    field.operation
  }=${getValueForQueryString(field)}`;

const toQueryStringFormat = (filters: FieldFilter[]): string =>
  [
    ...filters
      .filter((x) => !x.field.userCreated)
      .map(toNativeFieldFilterFormat)
      .map(nativeFieldToQueryStringFormat),
    ...filters
      .filter((x) => x.field.userCreated)
      .map(toCustomFieldFilterFormat)
      .map(customFieldToQueryStringFormat),
  ].join('&');

const convertQueryStringToObject = (
  filterString: string,
): { [key: string]: string } => {
  const filterObject = {};

  filterString.split('&').forEach((x) => {
    const [key, value] = decodeURIComponent(x).split('=');

    if (Object.keys(filterObject).includes(key)) {
      if (!Array.isArray(filterObject[key])) {
        filterObject[key] = [filterObject[key], value];
      } else {
        filterObject[key] = [...filterObject[key], value];
      }
    } else {
      filterObject[key] = value;
    }
  });

  return filterObject;
};

export const ApiFilter = {
  toNativeFieldFilterFormat,
  toCustomFieldFilterFormat,
  parse,
  toQueryStringFormat,
  nativeFieldToQueryStringFormat,
  customFieldToQueryStringFormat,
  convertQueryStringToObject,
};

const toNativeFieldFilterFormatForGroups = (
  filter: GroupCriteria,
): NativeFieldFilter => {
  return {
    name: filter.criteria.field.fieldName,
    ...toApiFormat(filter.criteria),
  };
};

const toCustomFieldFilterFormatForGroups = (
  filter: GroupCriteria,
): CustomFieldFilter => {
  return {
    fieldId: filter.criteria.field.fieldId,
    ...toApiFormat(filter.criteria),
  };
};

const toQueryStringFormatForGroups = (filters: GroupCriteria[]): string =>
  [
    ...filters
      .filter((x) => !x.criteria.field.userCreated)
      .map(toNativeFieldFilterFormatForGroups)
      .map(nativeFieldToQueryStringFormat),
    ...filters
      .filter((x) => x.criteria.field.userCreated)
      .map(toCustomFieldFilterFormatForGroups)
      .map(customFieldToQueryStringFormat),
  ].join('&');

export const ApiGroupFilter = {
  toNativeFieldFilterFormatForGroups,
  toCustomFieldFilterFormatForGroups,
  toQueryStringFormatForGroups,
};
